import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import { downloadFile } from 'utils/AppHelper'
import APIService from 'api/APIService'
import { ID, JobStatus } from 'types/entity'
import Button from 'core/components/buttons/Button'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'

interface BulkInviteProps {
  onSuccess?: VoidFunction
}

const useBulkInvite = ({ onSuccess }: BulkInviteProps = {}) => {
  const queryClient = useQueryClient()
  const { data: currentTeam } = useCurrentTeam()
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const [localJobId, setJobId] = useLocalStorage('member_invite_job_id', '')
  const jobID = settings.pollingJobs.memberBulkInviteJobId || localJobId

  const { mutate: downloadCSVTemplate, status: templateStatus } = useMutation({
    mutationFn: () => {
      return APIService.invites.getCSVTemplate()
    },
    onSuccess: (data) => {
      const csvContent = `data:text/csv;charset=utf-8,${data}`
      const encodedURI = encodeURI(csvContent)
      downloadFile(encodedURI, 'invite-member-template')
    },
    onError: () => {
      notification.error('An error has occurred trying to download this file')
    }
  })

  useQueryCallbacks({
    queryKey: ['member-export-polling', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      if (response.status === 'finished') {
        notification.dismiss('memberBulkInviteJobId')
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          memberBulkInviteJobId: ''
        })
        notification.success({
          title: 'Invitations from CSV are sent',
          id: jobID
        })
        queryClient.invalidateQueries({
          queryKey: ['teams', currentTeam?.token_id, 'invitations']
        })
      }

      if (response.status === 'failed') {
        notification.dismiss('memberBulkInviteJobId')
        notification.error({
          title: 'There were some errors with your file',
          subtitle: response.data.message as string,
          id: jobID,
          footer: () => {
            return (
              <Button
                className="text-primary"
                variant="link"
                onClick={() => {
                  downloadCSVTemplate()
                }}
              >
                Download CSV template
              </Button>
            )
          }
        })
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          memberBulkInviteJobId: ''
        })
      }
    },
    onError: () => {
      notification.dismiss('memberBulkInviteJobId')
      notification.error({
        title: 'Could not invite members at this time',
        id: jobID
      })
      setJobId('')
      set('pollingJobs', {
        ...settings.pollingJobs,
        memberBulkInviteJobId: ''
      })
    }
  })

  const uploadQuery = useMutation({
    mutationKey: ['upload', 'invite', 'csv'],
    mutationFn: ({ csv, teamId }: { csv: File; teamId: ID }) => {
      return APIService.invites.uploadCsvBulkInvite({
        file: csv as File,
        teamId
      })
    },
    onSuccess: (data) => {
      notification.loading({
        title: 'Sending invites...',
        subtitle:
          'This may take a few minutes. You can close this pop up and we’ll keep working in the background.',
        duration: Infinity,
        id: 'memberBulkInviteJobId'
      })
      setTimeout(() => {
        set('pollingJobs', {
          ...settings.pollingJobs,
          memberBulkInviteJobId: data.job_token_id
        })
      }, 0)
      setJobId(data.job_token_id)
      onSuccess?.()
    },
    onError: () => {
      notification.error('An error has occurred trying to upload your file')
    }
  })

  return {
    downloadCSVTemplate,
    uploadQuery,
    templateStatus
  }
}

export default useBulkInvite
